import { useQuery } from '@tanstack/react-query';
import { Ellipsis, KeyRound, Mail, Pencil, Plus, Trash2, UserRoundCog } from 'lucide-react';
import { parseAsString, useQueryStates } from 'nuqs';
import { useState } from 'react';
import { Link } from 'react-router';
import { PLATFORM_USER_STATUSES, PLATFORM_USER_STATUS_LABEL, type PlatformOrgRow, type PlatformPage, type PlatformUserRow } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { useTableParams } from '@/components/shared/use-table-params';
import { Pill } from '@/features/finance/pill';
import { api } from '@/lib/api';
import { AddUserModal } from './add-user-modal';
import { ImpersonateModal } from './impersonate-modal';
import { DeleteUserModal, EditUserModal, ResetLinkModal, SetPasswordModal } from './user-manage-modals';
import { dd, FilterSelect, useList, usePlatformAbility } from './shared';

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Proprietário' },
  { value: 'manager', label: 'Gerente' },
  { value: 'receptionist', label: 'Recepção' },
  { value: 'barber', label: 'Barbeiro' },
];

function useOrgOptions() {
  return useQuery({
    queryKey: ['platform', 'orgs', 'options'],
    queryFn: () => api.get<PlatformPage<PlatformOrgRow>>('/platform/orgs', { page: 1, pageSize: 200, sort: 'name', order: 'asc' }),
    staleTime: 60_000,
  });
}

const rowKey = (r: PlatformUserRow) => `${r.userId}:${r.organizationId ?? 'none'}`;

export function UsersPage() {
  const params = useTableParams({ sort: 'createdAt', order: 'desc' });
  const [f, setF] = useQueryStates({ organizationId: parseAsString, role: parseAsString, status: parseAsString }, { history: 'replace' });
  const { canModerate } = usePlatformAbility();
  const orgs = useOrgOptions();
  const [creating, setCreating] = useState(false);
  const [target, setTarget] = useState<PlatformUserRow | null>(null);
  const [manage, setManage] = useState<{ kind: 'edit' | 'reset' | 'password' | 'delete'; user: PlatformUserRow } | null>(null);
  const query = { ...params.query, organizationId: f.organizationId ?? undefined, role: f.role ?? undefined, status: f.status ?? undefined };
  const list = useList<PlatformPage<PlatformUserRow>>('users', '/platform/users', query);

  const columns: DataColumn<PlatformUserRow>[] = [
    {
      id: 'name',
      header: 'Usuário',
      meta: { sortKey: 'name', className: 'min-w-0 max-w-[16rem] whitespace-normal' },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span className="block min-w-0">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium break-words">{r.name}</span>
              {r.isPlatformAdmin && <Pill tone="warn">Super admin</Pill>}
              {r.organizationId && r.membershipActive === false && <Pill tone="neutral">Inativo</Pill>}
            </span>
            <span className="block text-xs break-all text-muted-foreground">{r.email}</span>
            {/* no celular as colunas de organização e papel colapsam aqui */}
            <span className="mt-0.5 block text-xs text-muted-foreground md:hidden">
              {r.organizationId ? `${r.organizationName} · ${r.roleName ?? '—'}` : 'Sem organização'}
            </span>
          </span>
        );
      },
    },
    {
      id: 'organization',
      header: 'Organização',
      meta: { className: 'hidden md:table-cell whitespace-normal' },
      cell: ({ row }) =>
        row.original.organizationId ? (
          <Link to={`/plataforma/organizacoes/${row.original.organizationId}`} className="font-medium underline-offset-2 hover:underline">
            {row.original.organizationName}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    { id: 'role', header: 'Papel', meta: { className: 'hidden md:table-cell' }, cell: ({ row }) => row.original.roleName ?? '—' },
    { id: 'email', header: 'E-mail', meta: { sortKey: 'email', className: 'hidden xl:table-cell' }, cell: ({ row }) => <span className="break-all">{row.original.email}</span> },
    { id: 'createdAt', header: 'Cadastro', meta: { sortKey: 'createdAt', className: 'hidden lg:table-cell' }, cell: ({ row }) => <span className="tabular whitespace-nowrap">{dd(row.original.createdAt)}</span> },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      meta: { align: 'right' },
      cell: ({ row }) => {
        const r = row.original;
        if (!canModerate || r.isPlatformAdmin) return null;
        const canImpersonate = Boolean(r.organizationId && r.membershipActive);
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" aria-label={`Ações de ${r.name}`}>
                <Ellipsis className="size-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canImpersonate && (
                <>
                  <DropdownMenuItem onSelect={() => setTarget(r)} aria-label={`Entrar como ${r.name} em ${r.organizationName}`}>
                    <UserRoundCog className="size-4" aria-hidden /> Entrar como
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onSelect={() => setManage({ kind: 'edit', user: r })}>
                <Pencil className="size-4" aria-hidden /> Editar
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setManage({ kind: 'reset', user: r })}>
                <Mail className="size-4" aria-hidden /> Redefinir senha (enviar link)
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setManage({ kind: 'password', user: r })}>
                <KeyRound className="size-4" aria-hidden /> Definir senha
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setManage({ kind: 'delete', user: r })}>
                <Trash2 className="size-4" aria-hidden /> Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const reset = () => params.set({ page: 1 });
  return (
    <div>
      <PageHeader
        title="Usuários"
        description="Todos os usuários do sistema, um item por vínculo com uma barbearia."
        actions={
          canModerate && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden /> Novo usuário
            </Button>
          )
        }
      />
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={list.refetch}
        searchPlaceholder="Buscar por nome ou e-mail"
        empty={{ title: 'Nenhum usuário encontrado' }}
        filters={
          <>
            <FilterSelect label="Organização" value={f.organizationId} onChange={(v) => { setF({ organizationId: v }); reset(); }} options={(orgs.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }))} />
            <FilterSelect label="Papel" value={f.role} onChange={(v) => { setF({ role: v }); reset(); }} options={ROLE_OPTIONS} />
            <FilterSelect label="Situação" value={f.status} onChange={(v) => { setF({ status: v }); reset(); }} options={PLATFORM_USER_STATUSES.map((s) => ({ value: s, label: PLATFORM_USER_STATUS_LABEL[s] }))} />
          </>
        }
      />
      <AddUserModal open={creating} onOpenChange={setCreating} />
      {manage?.kind === 'edit' && (
        <EditUserModal key={manage.user.userId} user={manage.user} memberships={(list.data?.data ?? []).filter((x) => x.userId === manage.user.userId)} onClose={() => setManage(null)} />
      )}
      {manage?.kind === 'reset' && <ResetLinkModal key={manage.user.userId} user={manage.user} onClose={() => setManage(null)} />}
      {manage?.kind === 'password' && <SetPasswordModal key={manage.user.userId} user={manage.user} onClose={() => setManage(null)} />}
      {manage?.kind === 'delete' && <DeleteUserModal key={manage.user.userId} user={manage.user} onClose={() => setManage(null)} />}
      {target && target.organizationId && (
        <ImpersonateModal key={rowKey(target)} orgId={target.organizationId} orgName={target.organizationName ?? ''} initialUserId={target.userId} onClose={() => setTarget(null)} />
      )}
    </div>
  );
}
